import React, { useEffect, useState, useMemo } from "react";
import {
  ChevronRight,
  Clock,
  BookOpen,
  ClipboardCheck,
  Megaphone,
  FileText,
  BarChart2,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../../../../context/AuthContext";
import { getAllCourseAnnouncements } from "../../../../services/announcement.service";
import { getAllCourseAssignments } from "../../../../services/assignment.service";
import { useCourse } from "../../../../context/CourseContext";
import { useParams } from "react-router-dom";
import { useUtilityContext } from "../../../../context/UtilityContext";
import {
  card,
  microLabel,
  cardTitle,
  bodySecondary,
  accentBarCard,
  motionFadeUp,
  motionStaggerChild,
  iconSize,
  dividerInner,
  emptyState,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";

// ---------------------------------------------------------------------------
// QUICK-ACTION BUTTON
// ---------------------------------------------------------------------------

const QuickAction = ({ icon: Icon, label, accent, onClick }) => (
  <motion.button
    whileHover={{ y: -1 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={cn(
      "flex items-center gap-2.5 px-4 py-2.5 rounded-sm border transition-all text-left",
      "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700",
      "hover:border-accent1/40 hover:shadow-sm group"
    )}
  >
    <div className="flex-shrink-0 w-8 h-8 rounded-sm bg-accent1/10 dark:bg-accent1/20 flex items-center justify-center">
      <Icon className="h-4 w-4 text-accent1" />
    </div>
    <span className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 group-hover:text-accent1 transition-colors">
      {label}
    </span>
  </motion.button>
);

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

const TeacherHome = ({ setSelectedOption }) => {
  const { user } = useAuth();
  const { courseData } = useCourse();
  const { courseID: urlCourseID } = useParams();
  const { selectedCourseId, currentModuleIndex, setCurrentModuleIndex } =
    useUtilityContext();

  const courseID = urlCourseID || selectedCourseId;

  const [announcements, setAnnouncements] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await getAllCourseAnnouncements({ courseID });
      setAnnouncements(response.announcements);
    } catch (error) {
      console.error("Error fetching announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async () => {
    try {
      setAssignmentsLoading(true);
      const response = await getAllCourseAssignments({ courseID });
      setAssignments(response.assignments || []);
    } catch (error) {
      console.error("Error fetching assignments:", error);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  useEffect(() => {
    if (courseID) {
      fetchAnnouncements();
      fetchAssignments();
    }
  }, [courseID]);

  // Derived data
  const totalStudents = courseData?.students?.length || 0;
  const totalModules = courseData?.syllabus?.modules?.length || 0;
  const courseProgress = courseData?.overallCompletion || 0;
  const reviewedLectures = courseData?.reviewedLectureCount || 0;
  const totalLectures = courseData?.totalLectureCount || 0;

  const { ungradedCount, totalSubmissions, overdueAssignments, upcomingAssignments } =
    useMemo(() => {
      let ungraded = 0;
      let submissions = 0;
      const overdue = [];
      const upcoming = [];
      const now = new Date();

      assignments.forEach((a) => {
        const turnedIn = a.stats?.turnedIn || 0;
        const graded = a.stats?.graded || 0;
        submissions += turnedIn;
        ungraded += turnedIn - graded;

        if (a.dueDate) {
          const due = new Date(a.dueDate);
          if (due < now && turnedIn - graded > 0) {
            overdue.push(a);
          } else if (due >= now) {
            upcoming.push(a);
          }
        }
      });

      upcoming.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

      return {
        ungradedCount: ungraded,
        totalSubmissions: submissions,
        overdueAssignments: overdue,
        upcomingAssignments: upcoming.slice(0, 3),
      };
    }, [assignments]);

  // Attention stats — aggregated counts
  const attentionStats = useMemo(() => {
    const pendingCount = assignments.reduce((sum, a) => {
      const assigned = a.stats?.assigned || a.stats?.total || totalStudents || 0;
      const turnedIn = a.stats?.turnedIn || 0;
      return sum + (assigned - turnedIn);
    }, 0);

    return {
      overdue: overdueAssignments.length,
      pending: pendingCount,
      needGrading: ungradedCount,
      hasItems: overdueAssignments.length > 0 || ungradedCount > 0 || pendingCount > 0,
    };
  }, [assignments, overdueAssignments, ungradedCount, totalStudents]);

  const getTimeAgo = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <motion.div {...motionFadeUp} className="mx-auto space-y-6 p-4 sm:p-6">
      {/* ----------------------------------------------------------------- */}
      {/* QUICK ACTIONS BAR */}
      {/* ----------------------------------------------------------------- */}
      <div className="flex flex-wrap gap-2">
        <QuickAction
          icon={ClipboardCheck}
          label="Mark Attendance"
          onClick={() => setSelectedOption("Mark Attendance")}
        />
        <QuickAction
          icon={FileText}
          label="Assignments"
          onClick={() => setSelectedOption("All Assignments")}
        />
        <QuickAction
          icon={Megaphone}
          label="Announcements"
          onClick={() => setSelectedOption("Announcements")}
        />
        <QuickAction
          icon={BarChart2}
          label="Gradebook"
          onClick={() => setSelectedOption("Gradebook")}
        />
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* STAT CARDS — compact, data-rich */}
      {/* ----------------------------------------------------------------- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Students */}
        <motion.div
          {...motionStaggerChild(0)}
          className={cn(card, "p-4 sm:p-5 overflow-hidden")}
        >
          <div className={accentBarCard} />
          <div className="mt-3">
            <p className={microLabel}>Students</p>
            <h3 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900 dark:text-white mt-1">
              {totalStudents}
            </h3>
          </div>
          <p className={cn(bodySecondary, "mt-2")}>enrolled in course</p>
        </motion.div>

        {/* Progress */}
        <motion.div
          {...motionStaggerChild(1)}
          className={cn(card, "p-4 sm:p-5 overflow-hidden")}
        >
          <div className={accentBarCard} />
          <div className="mt-3">
            <p className={microLabel}>Progress</p>
            <h3 className="text-2xl sm:text-3xl font-black tracking-tight text-accent1 mt-1">
              {courseProgress}%
            </h3>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-sm overflow-hidden">
              <div
                className="h-full bg-accent1 rounded-sm transition-all duration-500"
                style={{ width: `${courseProgress}%` }}
              />
            </div>
            <span className={cn(bodySecondary, "flex-shrink-0")}>
              {reviewedLectures}/{totalLectures}
            </span>
          </div>
        </motion.div>

        {/* Assignments */}
        <motion.div
          {...motionStaggerChild(2)}
          className={cn(card, "p-4 sm:p-5 overflow-hidden")}
        >
          <div className={accentBarCard} />
          <div className="mt-3">
            <p className={microLabel}>Assignments</p>
            <h3 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900 dark:text-white mt-1">
              {assignments.length}
            </h3>
          </div>
          {ungradedCount > 0 ? (
            <p className="mt-2 text-xs font-bold text-amber-600 dark:text-amber-400">
              {ungradedCount} need grading
            </p>
          ) : (
            <p className={cn(bodySecondary, "mt-2")}>
              {totalSubmissions} total submissions
            </p>
          )}
        </motion.div>

        {/* Modules */}
        <motion.div
          {...motionStaggerChild(3)}
          className={cn(card, "p-4 sm:p-5 overflow-hidden")}
        >
          <div className={accentBarCard} />
          <div className="mt-3">
            <p className={microLabel}>Modules</p>
            <h3 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900 dark:text-white mt-1">
              {totalModules}
            </h3>
          </div>
          <p className={cn(bodySecondary, "mt-2")}>in syllabus</p>
        </motion.div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* TWO-COLUMN LAYOUT */}
      {/* ----------------------------------------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT — 3/5 width */}
        <div className="lg:col-span-3 space-y-6">
          {/* ASSIGNMENTS — summary stats + recent list */}
          <motion.div
            {...motionStaggerChild(0, 0.2)}
            className={cn(card, "overflow-hidden")}
          >
            <div className={cn("px-5 py-4 border-b flex items-center justify-between", dividerInner)}>
              <h2 className={cardTitle}>Assignments</h2>
              <button
                onClick={() => setSelectedOption("All Assignments")}
                className="text-accent1 hover:text-accent1/80 text-xs font-bold uppercase tracking-wider flex items-center gap-1 transition-colors"
              >
                View All
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Stats strip */}
            <div className={cn("grid grid-cols-3 divide-x border-b", dividerInner, "divide-gray-200 dark:divide-gray-700")}>
              <div className="px-5 py-3.5 text-center">
                <p className={cn(
                  "text-lg font-black tracking-tight",
                  attentionStats.overdue > 0 ? "text-red-500 dark:text-red-400" : "text-gray-300 dark:text-gray-600"
                )}>
                  {attentionStats.overdue}
                </p>
                <p className={cn(microLabel, "mt-0.5 text-[10px]")}>Overdue</p>
              </div>
              <div className="px-5 py-3.5 text-center">
                <p className={cn(
                  "text-lg font-black tracking-tight",
                  attentionStats.pending > 0 ? "text-amber-500 dark:text-amber-400" : "text-gray-300 dark:text-gray-600"
                )}>
                  {attentionStats.pending}
                </p>
                <p className={cn(microLabel, "mt-0.5 text-[10px]")}>Pending</p>
              </div>
              <div className="px-5 py-3.5 text-center">
                <p className={cn(
                  "text-lg font-black tracking-tight",
                  attentionStats.needGrading > 0 ? "text-accent1" : "text-gray-300 dark:text-gray-600"
                )}>
                  {attentionStats.needGrading}
                </p>
                <p className={cn(microLabel, "mt-0.5 text-[10px]")}>To Grade</p>
              </div>
            </div>

            {/* Assignment list */}
            {assignmentsLoading ? (
              <div className="p-5 space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse space-y-2">
                    <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded-sm" />
                    <div className="flex gap-3">
                      <div className="h-3 w-20 bg-gray-100 dark:bg-gray-700/60 rounded-sm" />
                      <div className="h-3 w-16 bg-gray-100 dark:bg-gray-700/60 rounded-sm" />
                    </div>
                  </div>
                ))}
              </div>
            ) : assignments.length === 0 ? (
              <div className={cn("m-5", emptyState)}>
                <FileText className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className={bodySecondary}>No assignments created yet.</p>
              </div>
            ) : (
              <div>
                {assignments.slice(0, 5).map((assignment, i) => {
                  const turnedIn = assignment.stats?.turnedIn || 0;
                  const assigned =
                    assignment.stats?.assigned ||
                    assignment.stats?.total ||
                    totalStudents ||
                    0;
                  const graded = assignment.stats?.graded || 0;
                  const ungraded = turnedIn - graded;
                  const submissionPct =
                    assigned > 0 ? Math.round((turnedIn / assigned) * 100) : 0;

                  const isPastDue =
                    assignment.dueDate && new Date(assignment.dueDate) < new Date();

                  return (
                    <div
                      key={assignment._id}
                      className={cn(
                        "px-5 py-4",
                        i > 0 && "border-t border-gray-100 dark:border-gray-700/50"
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-bold text-gray-900 dark:text-white truncate">
                            {assignment.title}
                          </h3>
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className={cn(bodySecondary, "flex items-center gap-1")}>
                              <Clock className="h-3 w-3" />
                              {assignment.dueDate
                                ? new Date(assignment.dueDate).toLocaleDateString(
                                    "en-US",
                                    { month: "short", day: "numeric" }
                                  )
                                : "No due date"}
                            </span>
                            <span className={bodySecondary}>
                              {turnedIn}/{assigned} submitted
                            </span>
                          </div>
                        </div>

                        {/* Grading badge */}
                        {ungraded > 0 ? (
                          <span
                            className={cn(
                              "flex-shrink-0 px-2.5 py-1 rounded-sm text-xs font-bold border",
                              isPastDue
                                ? "text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/30 dark:border-red-700"
                                : "text-amber-600 bg-amber-50 border-amber-200 dark:text-amber-400 dark:bg-amber-900/30 dark:border-amber-700"
                            )}
                          >
                            {ungraded} to grade
                          </span>
                        ) : turnedIn > 0 ? (
                          <span className="flex-shrink-0 px-2.5 py-1 rounded-sm text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 dark:text-emerald-400 dark:bg-emerald-900/30 dark:border-emerald-700">
                            All graded
                          </span>
                        ) : null}
                      </div>

                      {/* Submission bar */}
                      <div className="mt-2.5 flex items-center gap-2">
                        <div className="flex-1 h-1 bg-gray-100 dark:bg-gray-700 rounded-sm overflow-hidden">
                          <div
                            className="h-full bg-accent1/60 rounded-sm transition-all duration-300"
                            style={{ width: `${submissionPct}%` }}
                          />
                        </div>
                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 flex-shrink-0 tabular-nums">
                          {submissionPct}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>

        {/* RIGHT — 2/5 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* UPCOMING DEADLINES */}
          {upcomingAssignments.length > 0 && (
            <motion.div
              {...motionStaggerChild(0, 0.3)}
              className={cn(card, "overflow-hidden")}
            >
              <div className={cn("px-5 py-4 border-b", dividerInner)}>
                <h2 className={cardTitle}>Upcoming Deadlines</h2>
              </div>
              <div className={cn("divide-y", dividerInner)}>
                {upcomingAssignments.map((a, i) => {
                  const dueDate = new Date(a.dueDate);
                  const daysLeft = Math.ceil(
                    (dueDate - new Date()) / (1000 * 60 * 60 * 24)
                  );

                  return (
                    <div
                      key={a._id}
                      className="px-5 py-3.5 flex items-center gap-3"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-sm bg-accent1/10 dark:bg-accent1/20 flex flex-col items-center justify-center">
                        <span className="text-xs font-black text-accent1 leading-none">
                          {dueDate.getDate()}
                        </span>
                        <span className="text-[9px] font-bold text-accent1/70 uppercase">
                          {dueDate.toLocaleDateString("en-US", { month: "short" })}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                          {a.title}
                        </p>
                        <p
                          className={cn(
                            "text-xs font-medium mt-0.5",
                            daysLeft <= 2
                              ? "text-red-500 dark:text-red-400"
                              : daysLeft <= 5
                              ? "text-amber-500 dark:text-amber-400"
                              : "text-gray-500 dark:text-gray-400"
                          )}
                        >
                          {daysLeft === 0
                            ? "Due today"
                            : daysLeft === 1
                            ? "Due tomorrow"
                            : `${daysLeft} days left`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* COURSE MODULES — compact list */}
          <motion.div
            {...motionStaggerChild(1, 0.3)}
            className={cn(card, "overflow-hidden")}
          >
            <div className={cn("px-5 py-4 border-b flex items-center justify-between", dividerInner)}>
              <h2 className={cardTitle}>Modules</h2>
              <button
                onClick={() => setSelectedOption("Content")}
                className="text-accent1 hover:text-accent1/80 text-xs font-bold uppercase tracking-wider flex items-center gap-1 transition-colors"
              >
                Manage
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>

            {totalModules === 0 ? (
              <div className="p-5">
                <div className={emptyState}>
                  <BookOpen className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className={bodySecondary}>No modules yet.</p>
                  <button
                    onClick={() => setSelectedOption("Syllabus")}
                    className="mt-2 text-xs font-bold text-accent1 hover:text-accent1/80 uppercase tracking-wider transition-colors"
                  >
                    Create Syllabus
                  </button>
                </div>
              </div>
            ) : (
              <div className={cn("divide-y", dividerInner)}>
                {courseData.syllabus.modules.slice(0, 6).map((module, i) => (
                  <motion.button
                    key={module._id || i}
                    {...motionStaggerChild(i, 0.35)}
                    onClick={() => {
                      setSelectedOption("Content");
                      setCurrentModuleIndex(i);
                    }}
                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left group"
                  >
                    <div className="flex-shrink-0 w-7 h-7 rounded-sm bg-accent1/10 dark:bg-accent1/20 flex items-center justify-center">
                      <span className="text-xs font-black text-accent1">
                        {module.moduleNumber || i + 1}
                      </span>
                    </div>
                    <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-200 truncate group-hover:text-accent1 transition-colors">
                      {module.moduleTitle}
                    </span>
                    <span className={cn(bodySecondary, "flex-shrink-0")}>
                      {module.topics?.length || 0} topics
                    </span>
                  </motion.button>
                ))}

                {totalModules > 6 && (
                  <button
                    onClick={() => setSelectedOption("Content")}
                    className={cn(
                      "w-full px-5 py-3 text-center",
                      bodySecondary,
                      "hover:text-accent1 transition-colors font-medium"
                    )}
                  >
                    +{totalModules - 6} more modules
                  </button>
                )}
              </div>
            )}
          </motion.div>

          {/* ANNOUNCEMENTS — compact feed */}
          <motion.div
            {...motionStaggerChild(2, 0.3)}
            className={cn(card, "overflow-hidden")}
          >
            <div className={cn("px-5 py-4 border-b flex items-center justify-between", dividerInner)}>
              <h2 className={cardTitle}>Announcements</h2>
              <button
                onClick={() => setSelectedOption("Announcements")}
                className="text-accent1 hover:text-accent1/80 text-xs font-bold uppercase tracking-wider flex items-center gap-1 transition-colors"
              >
                All
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>

            {loading ? (
              <div className="p-5 space-y-4">
                {[1, 2].map((i) => (
                  <div key={i} className="animate-pulse space-y-2">
                    <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded-sm" />
                    <div className="h-3 w-1/3 bg-gray-100 dark:bg-gray-700/60 rounded-sm" />
                  </div>
                ))}
              </div>
            ) : announcements.length === 0 ? (
              <div className="p-5">
                <div className={emptyState}>
                  <Megaphone className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className={bodySecondary}>No announcements yet.</p>
                </div>
              </div>
            ) : (
              <div className={cn("divide-y", dividerInner)}>
                {announcements.slice(0, 3).map((announcement, i) => (
                  <div
                    key={announcement._id}
                    className="px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex gap-3"
                  >
                    <img
                      src={announcement.image?.imageUrl || "/announcement.png"}
                      alt=""
                      className="flex-shrink-0 w-14 h-14 rounded-sm object-cover border border-gray-200 dark:border-gray-700"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white line-clamp-1">
                          {announcement.title}
                        </h3>
                        <span className={cn(bodySecondary, "flex-shrink-0 text-[10px]")}>
                          {getTimeAgo(announcement.publishDate)}
                        </span>
                      </div>
                      <p className={cn(bodySecondary, "mt-1 line-clamp-2")}>
                        {announcement.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default TeacherHome;
